import React, { useState, useEffect } from "react";
import DateItem from "./component/dateItem";
import Filter from "./component/filter";
import type { Dayjs } from "dayjs";
import API from "../../config/api";
import { POST, GET } from "../../utils/apiCalls";
import { useSelector } from "react-redux";
import "./styles.scss";
import dayjs from "dayjs";

interface DateRange {
  from_date: string;
  to_date: string;
}

interface Branch {
  id: string;
  bname: string;
  fulladdress: string;
}

interface BranchData {
  key: string;
  date: string;
  totalAmount: number;
  totalOrders: number;
  branchId?: string;
}

const Report: React.FC = () => {
  const [reportData, setReportData] = useState<BranchData[]>([]);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    from_date: dayjs().startOf("year").format("YYYY-MM-DD"),
    to_date: dayjs().endOf("year").format("YYYY-MM-DD"),
  });
  const { user } = useSelector((state: any) => state.User);

  const fetchBranches = async () => {
    try {
      const response: any = await GET(API.BRANCHES_LIST, {});
      if (response?.status && response?.data) {
        const formattedBranches = response.data.map((item: any) => ({
          id: item.CompanyMaster.id,
          bname: item.CompanyMaster.bname,
          fulladdress: item.CompanyMaster.fulladdress,
        }));
        console.log("Fetched branches:", formattedBranches);
        setBranches(formattedBranches);
        // Select first branch by default if available
        if (formattedBranches.length > 0) {
          setSelectedBranch(formattedBranches[0]);
          return formattedBranches[0];
        }
        return formattedBranches;
      } else {
        console.log("No branch data in response");
        setBranches([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
      return [];
    }
  };

  const fetchBranchDetails = async (branchId: string) => {
    try {
      const response: any = await GET(`${API.BRANCH_DETAILD}${branchId}`, {});
      console.log("Branch details response:", response);
      return response?.data;
    } catch (error) {
      console.error("Error fetching branch details:", error);
      return null;
    }
  };

  const fetchReportData = async (branchId?: string) => {
    try {
      setLoading(true);
      console.log("Fetching report data for branchId:", branchId);

      // Validate branchId first
      if (!branchId || branchId === "all") {
        console.error("Invalid branchId provided");
        return;
      }

      // Find the branch in our branches list
      const selectedBranch = branches.find((branch) => branch.id === branchId);
      if (!selectedBranch) {
        console.error("Branch not found in branches list:", branchId);
        return;
      }

      // Construct URL with parameters
      const filter1 = `?adminId=${user?.id}&companyId=${user?.staff?.companyid}&staffId=${user?.staff?.id}&from_date=${dateRange.from_date}&to_date=${dateRange.to_date}`;
      const filter2 = selectedBranch.id ? `&branchId=${selectedBranch.id}` : "";
      const url = `${API.REPORT_LIST}${filter1}${filter2}`;

      console.log("API request URL:", url);
      const response: any = await GET(url, {});
      console.log("API Response:", response);

      if (response?.status && response?.data) {
        // Sort data by month
        const sortedData = response.data.sort(
          (a: any, b: any) =>
            dayjs(a.month).valueOf() - dayjs(b.month).valueOf()
        );

        const formattedData: BranchData[] = sortedData.map((item: any) => ({
          key: item.month,
          date: dayjs(item.month).startOf("month").format("YYYY-MM-DD"),
          totalOrders: item.totalOrders || 0,
          totalAmount: parseFloat(String(item.totalSales)) || 0,
          branchId: selectedBranch.id,
        }));

        // Verify all data belongs to the requested branch
        const allDataForBranch = formattedData.every(
          (item) => item.branchId === selectedBranch.id
        );
        if (!allDataForBranch) {
          console.warn(
            "Some data does not match the requested branch:",
            selectedBranch.id
          );
        }

        console.log("Formatted report data:", formattedData);
        setReportData(formattedData);
      } else {
        console.log("No data in response");
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (filters: { branchId?: string }) => {
    console.log("Filter changed with:", filters);

    if (filters.branchId === "all") {
      console.log("Setting to all branches");
      setSelectedBranch(null);
      await fetchReportData("all");
    } else {
      const branch = branches.find((b) => b.id === filters.branchId);
      console.log("Selected branch:", branch);
      if (branch) {
        setSelectedBranch(branch);
        // Fetch branch details and report data
        const branchDetails = await fetchBranchDetails(branch.id);
        console.log("Branch details:", branchDetails);
        await fetchReportData(branch.id);
      }
    }
  };

  // Calculate totals for the selected branch
  const calculateTotals = () => {
    if (!reportData.length) return { totalAmount: 0, totalOrders: 0 };

    return reportData.reduce(
      (acc, curr) => ({
        totalAmount: acc.totalAmount + curr.totalAmount,
        totalOrders: acc.totalOrders + curr.totalOrders,
      }),
      { totalAmount: 0, totalOrders: 0 }
    );
  };

  const totals = calculateTotals();

  // Fetch initial data when component mounts
  useEffect(() => {
    const initializeReport = async () => {
      console.log("Initializing report...");
      const firstBranch = await fetchBranches();
      if (firstBranch) {
        await fetchReportData(firstBranch.id);
      }
    };
    initializeReport();
  }, []);

  // Add new useEffect to handle date range and branch changes
  useEffect(() => {
    if (selectedBranch?.id) {
      fetchReportData(selectedBranch.id);
    }
  }, [dateRange, selectedBranch]);

  return (
    <div className="report-container">
      <h3>Sales Report</h3>
      <div className="report-summary">
        {selectedBranch ? (
          <div>
            <h3>{selectedBranch.bname}</h3>
            <p>{selectedBranch.fulladdress}</p>
          </div>
        ) : (
          <h3>All Branches</h3>
        )}
      </div>
      {user?.staff?.staffAccess?.includes("administrator") ? (
        <Filter
          onFilterChange={handleFilterChange}
          branches={branches}
          branchId={selectedBranch}
          setBranch={setSelectedBranch}
          date={dateRange}
        />
      ) : null}

      <DateItem data={reportData} loading={loading} />
    </div>
  );
};

export default Report;
